import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import throttle from 'lodash/throttle';
import './index.scss';
import { ApolloProvider } from '@apollo/client';
import reportWebVitals from './reportWebVitals';
import { store } from './app/store';
import { PERSISTED_BOOKING_SLICES, saveState } from './app/localStorage';
import { syncSiteSession } from './util/siteSession';
import Router from './routes/Router';
import ConsentBar from './components/ConsentBar';
import SamChat from './components/SamChat';
import useAuthenticatedApolloClient from './hooks/useAuthenticatedApolloClient';
import { bootTheme } from './util/bootTheme';
import {
  initAnalytics, mp, registerAttribution, registerTrafficType, stopAnalytics,
} from './util/analytics';
import { identifyFromSession } from './app/identityAnalyticsListener';
import {
  attributionSuperProperties,
  initAperioTracking,
  refreshAperioConsent,
  setTrackingContext,
  snapshotLanding,
} from './util/aperioTracking';
import {
  getAbArm, getSkin, initSkin, subscribeSkin,
} from './consumer/skin';
import { normalizeThemePreset } from './util/themePreset';
import { TenantContext } from './components/TenantContext';
import { initPortalLanguage } from './i18n';
import { capturePromoParam } from './util/promoParam';
import { presetBrand } from './util/presetBrand';
import { goOilToAperioUrl } from './util/goOilRedirect';
import PortalLocalizationProvider from './i18n/PortalLocalizationProvider';

initAnalytics();

// The landing URL's campaign params, read before the booking flow strips
// any of them. Only read here; acted on below for the Aperio preset only.
const landing = snapshotLanding();
// `?promo=CODE` on any link fills the code in (util/promoParam).
capturePromoParam();

// The marketing site's "still signed in" hint (util/siteSession), set now
// for a login carried over from an earlier visit, and kept in step below.
syncSiteSession(store.getState().auth);

store.subscribe(throttle(() => {
  const state = store.getState();
  syncSiteSession(state.auth);
  saveState(
    {
      auth: state.auth,
    },
    Object.fromEntries(
      PERSISTED_BOOKING_SLICES.map((key) => [key, state[key]]),
    ),
  );
}, 1000));

interface ApolloClientProps {
  children: React.ReactNode;
}

function Apollo({ children }: ApolloClientProps) {
  const client = useAuthenticatedApolloClient();
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
bootTheme().then(async (cfg) => {
  // Go Oil's booking pages now live at Aperio (util/goOilRedirect): sent
  // over before anything renders or tracks. Staff, fleet and every other
  // tenant carry on below.
  const movedTo = goOilToAperioUrl({
    subdomain: cfg?.subdomain,
    host: window.location.hostname,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    roles: store.getState().auth.currentUser?.roles,
  });
  if (movedTo) {
    window.location.replace(movedTo);
    return;
  }
  const aperioPreset = normalizeThemePreset(cfg?.themePreset) === 'aperio';
  // The booking portal in French or Spanish (QA 2026-09-25 #6): Aperio
  // preset only. Picked and loaded before the first render, so a French
  // visitor never sees a flash of English. Every other tenant, Go Oil
  // included, stays in English and never fetches a translation.
  if (normalizeThemePreset(cfg?.themePreset) === 'aperio') {
    await initPortalLanguage(presetBrand(cfg?.themePreset)?.name);
  }
  // Aperio booking attribution + ad tags (util/aperioTracking). Every other
  // tenant, Go Oil included, never switches it on.
  if (normalizeThemePreset(cfg?.themePreset) === 'aperio') {
    // Which booking skin (consumer or today's flow) and A/B arm this visit
    // gets (consumer/skin.ts), on every GA4 and Mixpanel event from now on
    // - the first page_view included, so it is known before the tags start.
    // Read before the address hand-off strips the URL.
    initSkin({ roles: store.getState().auth.currentUser?.roles });
    setTrackingContext({ skin: getSkin(), abArm: getAbArm() });
    // A saved staff / admin session marks this device internal before any
    // tag loads (aperioTracking readInternalFlag).
    initAperioTracking(landing, { roles: store.getState().auth.currentUser?.roles });
    // The last-touch UTMs / click ids (utm_source … oppref) and the first
    // touch (initial_*) as super properties, from this landing and the
    // `apr_attr` cookie the marketing site shares - analytics consent only;
    // and our own traffic (QA / internal flag) as internal=true.
    registerAttribution(attributionSuperProperties());
    registerTrafficType();
    const tagSkin = () => {
      setTrackingContext({ skin: getSkin(), abArm: getAbArm() });
      mp.register({ skin: getSkin(), ab_arm: getAbArm() });
    };
    tagSkin();
    subscribeSkin(tagSkin);
  }
  // A saved session (every tenant): tie this visit to the user's id. Login,
  // sign-up and logout are handled by app/identityAnalyticsListener. After
  // the attribution above, so the profile's first touch can use it.
  identifyFromSession(store.getState(), cfg);
  // The portal's own consent bar (no apr_consent decision on file): a grant
  // starts Mixpanel and the tags now, without a reload, with the same super
  // properties as a visitor who arrived with the site's cookie; a decline of
  // an implied grant stops them.
  const consentGranted = () => {
    initAnalytics();
    // Consent first (it writes apr_attr from the landing kept in memory),
    // then the super properties that depend on it.
    refreshAperioConsent(landing);
    registerAttribution(attributionSuperProperties());
    registerTrafficType();
    mp.register({ skin: getSkin(), ab_arm: getAbArm() });
    identifyFromSession(store.getState(), cfg);
  };
  const consentDeclined = () => {
    stopAnalytics();
    refreshAperioConsent(landing);
  };
  ReactDOM.render(
    <React.StrictMode>
      <PortalLocalizationProvider>
        <Provider store={store}>
          <BrowserRouter>
            <TenantContext.Provider value={cfg}>
              <Apollo>
                <Router />
                {aperioPreset && <SamChat />}
              </Apollo>
            </TenantContext.Provider>
          </BrowserRouter>
          {aperioPreset && (
            <ConsentBar onGranted={consentGranted} onDeclined={consentDeclined} />
          )}
        </Provider>
      </PortalLocalizationProvider>
    </React.StrictMode>,
    document.getElementById('root'),
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
